import React, { useState } from 'react';
import '../styles/Reports.css';
import { Badge, Button } from './';
import { SortArrows } from '../assets/icons';
import { colour, formatterLong } from '../styles/GlobalStyles';

function ReportsTable({
    data,
    sortColumn,
    sortDirection,
    hoveredColumn,
    onSort,
    onHoverColumn,
    onRowClick,
    actionButtons = false,
    onTrackClick,
    onCloneClick,
    cloningPropertyId,
    companyId
}) {
    const [hoveredReportRow, setHoveredReportRow] = useState(null);

    // Formatting functions
    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        return formatterLong.format(value).replace('.00', '');
    };

    const formatPercentage = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        return `${value.toFixed(2)}%`;
    };

    const formatDateTime = (date) => {
        if (!date) return '-';
        
        let dateObj;
        
        if (date && typeof date.toDate === 'function') {
            dateObj = date.toDate();
        } else if (date && typeof date === 'object' && date._seconds !== undefined) {
            const milliseconds = date._seconds * 1000 + (date._nanoseconds || 0) / 1000000;
            dateObj = new Date(milliseconds);
        } else if (date instanceof Date) {
            dateObj = date;
        } else if (typeof date === 'number') {
            dateObj = new Date(date);
        } else if (typeof date === 'string') {
            dateObj = new Date(date);
        } else {
            return '-';
        }
        
        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        
        const now = new Date();
        const diffMs = now - dateObj;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        const isToday = dateObj.getDate() === now.getDate() &&
                    dateObj.getMonth() === now.getMonth() &&
                    dateObj.getFullYear() === now.getFullYear();
        
        const diffMonths = (now.getFullYear() - dateObj.getFullYear()) * 12 + 
                        (now.getMonth() - dateObj.getMonth());
        
        if (isToday && diffMinutes < 60) {
            return diffMinutes <= 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
        }
        
        if (diffHours < 24) {
            return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
        }
        
        if (diffMonths >= 12) {
            const diffYears = Math.floor(diffMonths / 12);
            return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
        }
        
        if (diffMonths >= 1) {
            return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
        }
        
        return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    };

    const formatAddress = (address) => {
        if (!address) return { line1: '-', line2: null };
        
        if (address.streetAddress) {
            const line1 = address.streetAddress;
            const line2Parts = [];
            if (address.city) line2Parts.push(address.city);
            if (address.state) line2Parts.push(address.state);
            if (address.zipcode) line2Parts.push(address.zipcode);
            const line2 = line2Parts.length > 0 ? line2Parts.join(', ') : null;
            return { line1, line2 };
        }
        
        if (address.label) {
            const commaMatch = address.label.match(/^(.+?)\s+([^,]+),\s*([A-Z]{2})\s+(\d{5}(-\d{4})?)$/);
            if (commaMatch) {
                return {
                    line1: commaMatch[1].trim(),
                    line2: `${commaMatch[2]}, ${commaMatch[3]} ${commaMatch[4]}`
                };
            }
            
            const parts = address.label.split(',').map(p => p.trim());
            if (parts.length >= 2) {
                return {
                    line1: parts[0],
                    line2: parts.slice(1).join(', ')
                };
            }
            
            return { line1: address.label, line2: null };
        }
        
        return { line1: '-', line2: null };
    };

    // Determine grid template columns based on actionButtons prop
    const gridColumns = actionButtons 
        ? '2fr 1fr 1fr 1fr 1fr 1fr 1fr 1.2fr 1.2fr 160px'
        : '2fr 1fr 1fr 1fr 1fr 1fr 1.2fr';

    return (
        <div className="reports-table">
            <div className="reports-table-header" style={{ gridTemplateColumns: gridColumns }}>
                <div 
                    className="reports-table-cell reports-table-header-cell body-regular colour-secondary" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('address')}
                    onMouseEnter={() => onHoverColumn('address')}
                    onMouseLeave={() => onHoverColumn(null)}
                >
                    <span>Address</span>
                    <SortArrows fill={sortColumn === 'address' ? colour.gray06 : (hoveredColumn === 'address' ? colour.gray04 : colour.gray00)} className={sortColumn === 'address' ? '' : 'opacity-50'} />
                </div>
                <div 
                    className="reports-table-cell reports-table-header-cell body-regular colour-secondary" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('arv')}
                    onMouseEnter={() => onHoverColumn('arv')}
                    onMouseLeave={() => onHoverColumn(null)}
                >
                    <span>ARV</span>
                    <SortArrows fill={sortColumn === 'arv' ? colour.gray06 : (hoveredColumn === 'arv' ? colour.gray04 : colour.gray00)} className={sortColumn === 'arv' ? '' : 'opacity-50'} />
                </div>
                {actionButtons && (
                    <div className="reports-table-cell reports-table-header-cell body-regular colour-secondary">
                        <span>Confidence</span>
                    </div>
                )}
                <div 
                    className="reports-table-cell reports-table-header-cell body-regular colour-secondary" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('repairCost')}
                    onMouseEnter={() => onHoverColumn('repairCost')}
                    onMouseLeave={() => onHoverColumn(null)}
                >
                    <span>Repair Cost</span>
                    <SortArrows fill={sortColumn === 'repairCost' ? colour.gray06 : (hoveredColumn === 'repairCost' ? colour.gray04 : colour.gray00)} className={sortColumn === 'repairCost' ? '' : 'opacity-50'} />
                </div>
                <div 
                    className="reports-table-cell reports-table-header-cell body-regular colour-secondary" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('purchasePrice')}
                    onMouseEnter={() => onHoverColumn('purchasePrice')}
                    onMouseLeave={() => onHoverColumn(null)}
                >
                    <span>Purchase Price</span>
                    <SortArrows fill={sortColumn === 'purchasePrice' ? colour.gray06 : (hoveredColumn === 'purchasePrice' ? colour.gray04 : colour.gray00)} className={sortColumn === 'purchasePrice' ? '' : 'opacity-50'} />
                </div>
                <div 
                    className="reports-table-cell reports-table-header-cell body-regular colour-secondary" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('profit')}
                    onMouseEnter={() => onHoverColumn('profit')}
                    onMouseLeave={() => onHoverColumn(null)}
                >
                    <span>Profit</span>
                    <SortArrows fill={sortColumn === 'profit' ? colour.gray06 : (hoveredColumn === 'profit' ? colour.gray04 : colour.gray00)} className={sortColumn === 'profit' ? '' : 'opacity-50'} />
                </div>
                <div 
                    className="reports-table-cell reports-table-header-cell body-regular colour-secondary" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('profitPercentage')}
                    onMouseEnter={() => onHoverColumn('profitPercentage')}
                    onMouseLeave={() => onHoverColumn(null)}
                >
                    <span>Profit %</span>
                    <SortArrows fill={sortColumn === 'profitPercentage' ? colour.gray06 : (hoveredColumn === 'profitPercentage' ? colour.gray04 : colour.gray00)} className={sortColumn === 'profitPercentage' ? '' : 'opacity-50'} />
                </div>
                <div 
                    className="reports-table-cell reports-table-header-cell body-regular colour-secondary" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('date')}
                    onMouseEnter={() => onHoverColumn('date')}
                    onMouseLeave={() => onHoverColumn(null)}
                >
                    <span>Date</span>
                    <SortArrows fill={sortColumn === 'date' ? colour.gray06 : (hoveredColumn === 'date' ? colour.gray04 : colour.gray00)} className={sortColumn === 'date' ? '' : 'opacity-50'} />
                </div>
                {actionButtons && (
                    <div className="reports-table-cell reports-table-header-cell body-regular colour-secondary">
                        <span>Email</span>
                    </div>
                )}
                {actionButtons && (
                    <div className="reports-table-cell reports-table-header-cell"></div>
                )}
            </div>
            {data.map((item, index) => {
                const arv = item.arvData?.arv;
                const confidenceLabel = item.arvData?.confidence?.confidenceLabel;
                const repairCost = item.repairCost?.totalRepairCost;
                const purchasePrice = item.purchasePrice;
                const profit = item.estimatedProfit;
                const profitPercentage = item.estimatedProfitPercentage;
                const addressLines = formatAddress(item.address);
                const emailNotification = item.profitEmailNotification;
                const emailSentAt = emailNotification?.sent === true ? emailNotification?.sentAt : null;
                
                const isHovered = hoveredReportRow === item.propertyId;
                const isCloning = cloningPropertyId === item.propertyId;
                
                return (
                    <div 
                        className="reports-table-row"
                        key={index}
                        style={{ gridTemplateColumns: gridColumns }}
                        onMouseEnter={() => setHoveredReportRow(item.propertyId)}
                        onMouseLeave={() => setHoveredReportRow(null)}
                        onClick={(e) => {
                            // Don't open if clicking on buttons
                            if (e.target.closest('.reports-table-actions-cell')) {
                                return;
                            }
                            if (onRowClick) {
                                onRowClick(e, item);
                            }
                        }}
                    >
                        <div className="reports-table-cell">
                            <div className="reports-address-container">
                                <span className="body-semibold colour-primary">{addressLines.line1}</span>
                                {addressLines.line2 && <span className="body-regular colour-secondary">{addressLines.line2}</span>}
                            </div>
                        </div>
                        <div className="reports-table-cell body-regular colour-primary">{formatCurrency(arv)}</div>
                        {actionButtons && (
                            <div className="reports-table-cell body-regular colour-primary">
                                {confidenceLabel || '-'}
                            </div>
                        )}
                        <div className="reports-table-cell body-regular colour-primary">{formatCurrency(repairCost)}</div>
                        <div className="reports-table-cell body-regular colour-primary">{formatCurrency(purchasePrice)}</div>
                        <div className={`reports-table-cell body-regular ${profit !== null && profit !== undefined ? (profit >= 0 ? 'profit-positive' : 'profit-negative') : 'colour-primary'}`}>
                            {formatCurrency(profit)}
                        </div>
                        <div className="reports-table-cell">
                            {profitPercentage !== null && profitPercentage !== undefined ? (
                                <Badge
                                    bgColour={profitPercentage >= 15 ? 'bg-colour-yellow-01' : profitPercentage >= 0 ? 'bg-colour-gray-02' : 'bg-colour-red-02'}
                                    text={formatPercentage(profitPercentage)}
                                    textClass={`body-regular colour-black`}
                                    statusDot={false}
                                    statusDotColour={null}
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={false}
                                />
                            ) : (
                                <span className="body-regular colour-primary">{formatPercentage(profitPercentage)}</span>
                            )}
                        </div>
                        <div className="reports-table-cell body-regular colour-primary">{formatDateTime(item.date)}</div>
                        {actionButtons && (
                            <div className="reports-table-cell body-regular colour-primary">
                                {emailSentAt ? formatDateTime(emailSentAt) : '-'}
                            </div>
                        )}
                        {actionButtons && (
                            <div className="reports-table-cell reports-table-actions-cell">
                                {isHovered && (
                                    <div className="reports-table-actions-buttons">
                                        <Button
                                            buttonType="primary-button"
                                            size="small"
                                            text="Track"
                                            cta={(e) => {
                                                e.stopPropagation();
                                                if (onTrackClick) {
                                                    onTrackClick(companyId, item.propertyId);
                                                }
                                            }}
                                            disabled={false}
                                            leftIcon={null}
                                            leftIconClass=""
                                            rightIcon={null}
                                            rightIconClass=""
                                            alt=""
                                            standardIcon={false}
                                            badge={false}
                                            badgeVal={null}
                                            badgeBG={null}
                                            buttonRef={null}
                                            spinner={false}
                                        />
                                        <Button
                                            buttonType="secondary-button"
                                            size="small"
                                            text={isCloning ? "Cloning..." : "Clone"}
                                            cta={(e) => {
                                                e.stopPropagation();
                                                if (onCloneClick) {
                                                    onCloneClick(item.propertyId);
                                                }
                                            }}
                                            disabled={isCloning}
                                            leftIcon={null}
                                            leftIconClass=""
                                            rightIcon={null}
                                            rightIconClass=""
                                            alt=""
                                            standardIcon={false}
                                            badge={false}
                                            badgeVal={null}
                                            badgeBG={null}
                                            buttonRef={null}
                                            spinner={isCloning}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default ReportsTable;

